// MobileSheet — delad bottom-sheet-scaffold för mobilens sub-sheets
// (verktygspalett, properties, …). Extraherad ur den duplicerade
// strukturen i MobileToolSheet + MobilePropertiesSheet (#38/#39,
// sub-plan Fas 8.2).
//
// Scaffolden = bottom-ankrad Sheet + rundad topp + sr-only-titel
// (a11y) + scroll-container med grab-handle-affordans +
// safe-area-inset-spacer för notch-telefoner.
//
// **Ren refactor:** renderar identisk DOM mot de tidigare inlinade
// versionerna. Enda skillnaden är att default-`maxHeight` flyttats
// från en Tailwind arbitrary-value-klass (`max-h-[80dvh]`) till inline
// `style` — samma computed `max-height`, men interpolerbart per
// call-site (Tailwind JIT kan inte se dynamiska arbitrary-values).

import type { CSSProperties, ReactNode } from 'react';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';

interface MobileSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** sr-only-namn som annonseras för skärmläsare. */
  title: string;
  /** Sheet-maxhöjd som dvh-fraktion. Default 80. Överskrivs om
   *  `contentStyle.maxHeight` sätts (t.ex. keyboard-offset-calc). */
  maxHeightDvh?: number;
  /** Extra style på SheetContent (t.ex. Android-keyboard-padding).
   *  Spreadas sist så call-sites kan överskriva maxHeight/paddingBottom. */
  contentStyle?: CSSProperties;
  children: ReactNode;
}

export function MobileSheet({
  open, onOpenChange, title, maxHeightDvh = 80, contentStyle, children,
}: MobileSheetProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="p-0 rounded-t-2xl flex flex-col"
        style={{ maxHeight: `${maxHeightDvh}dvh`, ...contentStyle }}
      >
        <SheetTitle className="sr-only">{title}</SheetTitle>
        <div className="flex-1 overflow-y-auto min-h-0" style={{ WebkitOverflowScrolling: 'touch' }}>
          {/* Drag handle */}
          <div className="flex justify-center py-2 flex-shrink-0">
            <div className="w-10 h-1 rounded-full bg-muted-foreground/30" />
          </div>
          {children}
        </div>
        <div className="flex-shrink-0" style={{ height: 'env(safe-area-inset-bottom, 0px)' }} />
      </SheetContent>
    </Sheet>
  );
}
